import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProducerById } from '@/hooks/useMyProducers';
import { useProducerProducts } from '@/hooks/useProducerProducts';
import { useToast } from '@/hooks/use-toast';
import { isPending } from '@/lib/producer/status';
import { ProductForm, ProductFormData } from '@/components/products';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { useDraftSessionId, clearDraftSessionId } from '@/hooks/useDraftSessionId';
import { finalizeDraftSession } from '@/lib/media/mediaApi';

/**
 * ProducerProductCreate – create-on-save pattern.
 *
 * No DB row is created until the user clicks Save.
 * Media uploads use a client-generated draftSessionId
 * and are finalized after the product row is inserted.
 */
export default function ProducerProductCreate() {
  const { farmId } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const { producer, isLoading: producerLoading } = useProducerById(farmId);
  const { createProduct } = useProducerProducts(farmId);
  const { toast } = useToast();

  const [isSaving, setIsSaving] = useState(false);

  const basePath = `/producer/farm/${farmId}/shop`;
  const draftKey = `product-create-${farmId ?? 'new'}`;
  const draftSessionId = useDraftSessionId(draftKey);

  const isPublishBlocked = producer ? isPending(producer.producerStatus) : false;

  const handleSave = async (data: ProductFormData, _publish: boolean) => {
    if (!data.title?.trim() || !farmId) {
      toast({
        title: 'Titel krävs',
        description: 'Ange en titel för produkten.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    try {
      const priceOre = Math.round(parseFloat(data.priceKr || '0') * 100);

      const product = await createProduct({
        title: data.title.trim(),
        description: data.description || undefined,
        price_ore: priceOre,
        status: data.status,
        product_type: data.productType,
        bundle_items: data.productType === 'bundle' ? data.bundleItems : undefined,
        seo_title: data.seoTitle || null,
        seo_description: data.seoDescription || null,
        seo_image_url: data.seoImageUrl || null,
        pickup_instructions: data.pickupInstructions || null,
        stock_limit: data.stockLimit,
      });

      // Finalize draft media → move from private-media to public-media
      if (product?.id) {
        try {
          await finalizeDraftSession(draftSessionId, 'product', product.id);
        } catch (err) {
          console.warn('[product-create] finalizeDraftSession failed (non-fatal):', err);
        }
      }

      // Clear stored draft session ID
      clearDraftSessionId(draftKey);

      toast({
        title: 'Produkt skapad',
        description: data.status === 'public'
          ? 'Produkten är nu publicerad och synlig för kunder.'
          : 'Produkten har sparats som utkast.',
      });

      navigate(`${basePath}/products`);
    } catch (err: any) {
      const isLimit = err?.isLimitError;
      const errorMsg = err?.message || '';
      const isRls = errorMsg.includes('row-level security') || errorMsg.includes('unauthorized');
      toast({
        title: isLimit
          ? 'Produktgräns nådd'
          : isRls
            ? 'Behörighet saknas'
            : 'Kunde inte spara',
        description: isLimit
          ? err.message
          : isRls
            ? 'Kontrollera att du är medlem i gården och försök igen.'
            : `Det gick inte att spara just nu. Försök igen om en stund.${import.meta.env.DEV ? ` (${errorMsg})` : ''}`,
        variant: 'destructive',
      });
      console.error('[product-create] Save failed:', { error: err, producerId: farmId });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`${basePath}/products`);
  };

  return (
    <ProducerLayout
      farmId={farmId || ''}
      producerName={producer?.name || 'Laddar...'}
      title="Skapa produkt"
      maxWidth="xl"
    >
      {farmId && (
        <ProductForm
          mode="create"
          producerId={farmId}
          draftSessionId={draftSessionId}
          shopifyConfigured={false}
          syncing={null}
          isSaving={isSaving}
          onSave={handleSave}
          onCancel={handleCancel}
          isPublishBlocked={isPublishBlocked}
        />
      )}
    </ProducerLayout>
  );
}
